import React, { useMemo, useState } from 'react';

import {
	FrontEngineIcon,
	FrontEngineDoubleIcon,
	RearEngineIcon,
	RearEngineDoubleIcon,
	Star,
} from '../Icons';
import DeckComponent from '../Deck'; 

import {
	TrainWrapper,
	Container,
	CarriageWrapper,
	IconWrapper,
	CarriageName,
	StarRectangleWrapper,
	CoachInfoWrapper,
  TrainMapWrapper, 
  TrainMapScrollContainer
} from './TrainMapStyled'; 

import TrainMapArrows from './TrainMapArrows';
import { DeckIndicatorType, DeckLevelType, DeckType } from '../seatMapConstants';

export const debounce = (func, wait = 100) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

const TrainMap = ({
	vehicle,
	selectedCoachId = '',
	selectedDeckId = '',
	deckLevel = '',
	setSelectedDeck,
  maxWidth,
	...rest
}) => {
	const [chevronsDisabled, setChevronsDisabled] = useState({
		left: true,
		right: false,
	});

	const handleClick =
		(
			coachId,
			deckId,
			currentDeckLevel,
			numAvailableSeats,
		) =>
		() => {
			if (numAvailableSeats) {
				setSelectedDeck({ coachId, deckId, deckLevel: currentDeckLevel });
			}
		};

	const isDoubleDecker = useMemo(
		() => vehicle?.coaches?.some((coach) => coach.decks.length > 1),
		[vehicle],
	);

	const sortedCoaches = useMemo(
		() => vehicle?.coaches?.sort((a, b) => Number(a.id) - Number(b.id)),
		[vehicle],
	);

	const showLegend = useMemo(
		() =>
			sortedCoaches.some((coach) =>
				coach?.decks?.some(
					({ description }) => description === DeckIndicatorType.BUSINESS_1ERE,
				),
			),
		[sortedCoaches],
	);

	const onScrollCallback = (e) => {
		const trainElement = e?.target;
		if (!trainElement) return;
		const containerElement = document.querySelector(
			'#train-map-scroll',
		);
		setChevronsDisabled({
			left: trainElement.scrollLeft <= 0,
			right:
				containerElement?.offsetWidth + trainElement.scrollLeft >=
				trainElement.scrollWidth,
		});
	};

	const onScrollCallbackDebounced = debounce(onScrollCallback, 50);

	return (
		<TrainMapWrapper>
			<TrainMapArrows
				chevronsDisabled={chevronsDisabled}
				isDoubleDecker={isDoubleDecker}
			/>
			<TrainMapScrollContainer
				id="train-map-scroll"
				onScroll={onScrollCallbackDebounced}
				maxWidth={maxWidth}
			>
				<TrainWrapper className="train-map" key={vehicle?.id} {...rest}>
					<IconWrapper>
						{isDoubleDecker ? <RearEngineDoubleIcon /> : <RearEngineIcon />}
					</IconWrapper>
					<Container>
						{sortedCoaches?.map((coach) => {
							const { decks, id: coachId } = coach;

							const coachName =
								decks.length > 1 && selectedCoachId === coachId
									? `${deckLevel} ${coachId}`
									: `${coachId}`;
							return (
								<CarriageWrapper key={coachId}>
									{decks?.map((deck, index) => {
										const {
											id: deckId,
											function: deckFunction,
											numAvailableSeats,
											description,
										} = deck;
										const isDeckAvailable =
											numAvailableSeats > 0 ||
											deckFunction === DeckType.BAR ||
											deckFunction === DeckType.DINING ||
											deckFunction === DeckType.UNSPECIFIED;

										const currentDeckLevel =
											isDoubleDecker && index === 0
												? DeckLevelType.UPPER
												: DeckLevelType.LOWER;

										const deckIndicator = description || '';

										return (
											<DeckComponent
												key={`${deckId}-${index}`}
												isDisabled={!isDeckAvailable}
												isDoubleDecker={isDoubleDecker}
												deckFunction={deckFunction}
												handleClick={handleClick(
													coachId,
													deckId,
													currentDeckLevel,
													numAvailableSeats,
												)}
												isActive={selectedDeckId === deckId}
												deckIndicator={deckIndicator}
											/>
										);
									})}
									<CarriageName>{coachName}</CarriageName>
								</CarriageWrapper>
							);
						})}
					</Container>
					<IconWrapper>
						{isDoubleDecker ? <FrontEngineDoubleIcon /> : <FrontEngineIcon />}
					</IconWrapper>
				</TrainWrapper>
				{showLegend && (
					// Legend under trainmap
					<CoachInfoWrapper className="trainmap-legend">
						<StarRectangleWrapper width={24}>
							<Star height={9} width={9} />
						</StarRectangleWrapper>
						<h1>Business Premiere</h1>
					</CoachInfoWrapper>
				)}
			</TrainMapScrollContainer>
		</TrainMapWrapper>
	);
};

export default TrainMap;
